import React from 'react';
import { Link } from 'react-router-dom';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { cx } from '../shared/ui/utils';

const formatTravellers = (pkg) => {
  const min = pkg.min_persons || pkg.minPersons;
  const max = pkg.max_persons || pkg.maxPersons || pkg.max_guests || pkg.maxGuests;
  if (min && max) return `${min}-${max} travellers`;
  if (max) return `Up to ${max} travellers`;
  if (min) return `From ${min} travellers`;
  return 'Flexible group';
};

const PackageCard = ({
  pkg,
  view = 'grid',
  compact = false,
  onEnquire,
  labels = {},
  formatPrice,
  linkQuery,
  showQuickBadges = true,
  showEnquiryCta = true,
  showReviewsCount = true,
}) => {
  const image = resolveAssetUrl(
    pkg.card_image || pkg.cardImage || pkg.thumbnail_image || pkg.thumbnailImage || pkg.cover_image || pkg.coverImage || pkg.image_url || pkg.image
  );
  const themeName = pkg.themes?.[0]?.name || pkg.theme_name || '';
  const priceValue = pkg.price_from || pkg.price_per_person || 0;
  const priceLabel = formatPrice ? formatPrice(priceValue, pkg.currency || 'INR') : priceValue;
  const originalPrice = pkg.original_price || pkg.price_original || pkg.originalPrice;
  const summary = pkg.short_description || pkg.subtitle || pkg.tagline || '';
  const layoutClass = view === 'list' ? 'flex flex-col sm:flex-row' : 'flex h-full flex-col';
  const imageClass = view === 'list' ? 'h-56 sm:h-auto sm:w-[36%]' : 'h-56 w-full';
  const detailHref = linkQuery
    ? `/packages/${pkg.slug}${linkQuery.startsWith('?') ? linkQuery : `?${linkQuery}`}`
    : `/packages/${pkg.slug}`;

  return (
    <div
      className={cx(
        'group min-w-0 overflow-hidden rounded-[28px] border border-[#dde3ef] bg-[#fffdfc] shadow-[0_16px_40px_rgba(15,23,42,0.06)] transition hover:-translate-y-1 hover:shadow-[0_24px_54px_rgba(15,23,42,0.12)]',
        layoutClass,
        compact && 'shadow-none'
      )}
    >
      <div className={cx('relative shrink-0 overflow-hidden', imageClass)}>
        <img
          src={image || 'https://picsum.photos/seed/homevilas-packages/800/600'}
          alt={pkg.title}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/45 via-transparent to-transparent" />
      </div>
      <div className={cx('flex min-w-0 flex-1 flex-col gap-3 p-4', compact && 'gap-2.5')}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8b97a7]">
              {pkg.destination_name || pkg.destination_city || 'Curated escape'}
            </p>
            <h3 className="mt-1.5 line-clamp-2 break-words text-[20px] font-semibold leading-tight text-[#142033]">
              {pkg.short_title || pkg.title}
            </h3>
          </div>
          <div className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            <i className="bi bi-star-fill mr-1 text-[10px]" />
            {Number(pkg.rating || 0).toFixed(1)}
          </div>
        </div>
        {summary ? <p className="line-clamp-2 text-sm leading-5 text-slate-600">{summary}</p> : null}
        {showQuickBadges ? (
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="rounded-full bg-[#f4e7cf] px-2.5 py-0.5 text-[#8a6428]">
              <i className="bi bi-clock mr-1 text-[10px]" />
              {pkg.duration_days || pkg.days || 0} days
            </span>
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5">
              <i className="bi bi-people mr-1 text-[10px]" />
              {formatTravellers(pkg)}
            </span>
            {themeName ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5">
                <i className="bi bi-tag mr-1 text-[10px]" />
                {themeName}
              </span>
            ) : null}
          </div>
        ) : null}
        {showReviewsCount ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <i className="bi bi-chat-square-text" />
            <span>{pkg.review_count || pkg.reviewCount || 0} reviews</span>
          </div>
        ) : null}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-[#efe7dc] pt-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8b97a7]">From</p>
            <div className="flex items-center gap-2">
              <p className="text-[1.75rem] font-semibold leading-none text-[#0f766e]">{priceLabel}</p>
              {originalPrice ? (
                <p className="text-xs text-slate-400 line-through">{formatPrice?.(originalPrice, pkg.currency)}</p>
              ) : null}
            </div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#8b97a7]">{labels.perPerson || 'per person'}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <Link
              to={detailHref}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full border border-[#d7cfbf] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-700 hover:border-[#c9a15b] hover:text-[#10233d]"
            >
              {pkg.detail_cta_label || labels.viewDetails || 'View details'}
            </Link>
            {showEnquiryCta ? (
              <button
                type="button"
                onClick={() => onEnquire?.(pkg)}
                className="rounded-full bg-[#081b3a] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#0f766e]"
              >
                {pkg.enquire_button_label || labels.enquire || 'Enquire'}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageCard;
